/**
 * Sidebar navigation configuration — Karr Design System.
 *
 * Architecture:  v4 — "User-First Information Architecture"
 * ─────────────────────────────────────────────────────────────
 * Reorganised around user intent, not feature taxonomy.
 *
 * Zones:
 *   1. CORE       — Daily workflow (sales, purchases, banking)
 *   2. ACCOUNTING — Books, assets, tax, reports
 *   3. MODULES    — Optional feature add-ons
 *   4. TOOLS      — Productivity & AI tools
 *   5. SYSTEM     — Settings, integrations, practice
 *
 * Based on QBO 2026 / Xero Sep-2025 / Zoho Books patterns,
 * refined with user feedback (Feb 2026).
 *
 * @since 3.0.0
 */
import type { ReactNode } from 'react';
import {
    LayoutDashboard, FileText, Receipt, CreditCard, ArrowLeftRight,
    Users, Package, Landmark, BarChart3, FileCheck,
    Calculator, Shield, Bot, ClipboardList, RotateCcw, Truck, RefreshCw,
    ShoppingCart, PackageCheck, Link2, Briefcase, Warehouse, Target, Clock, Globe,
    ArrowDownUp, ClipboardCheck, Heart, Table2, Building2,
    Send, ScanLine, Utensils, ChefHat,
    Wrench, ListChecks, UserCircle, Factory, ShieldCheck, FolderKanban, FileQuestion, CalendarDays, LifeBuoy, BadgeCheck, Layers3, Store, FileStack, Repeat, Gift, Wallet,
    BookMarked, MapPinned, FlaskConical, Lock, Wand2, Wheat,
    Gavel, Code2, TrendingUp, Plane, UserRound, BedDouble, Handshake, CalendarRange, FileSpreadsheet, GraduationCap, Gem, Toolbox,
    Scissors, CalendarClock, Sunrise, UserSquare2,
} from 'lucide-react';
import { IN_COMPANY_ORG_TYPES } from '../config/countryConfig';

/* ─── Types ──────────────────────────────────────────────────── */

/** A single navigation item (leaf node) */
export interface NavItem {
    id: string;
    label: string;
    icon: ReactNode;
    href: string;
    badge?: number;
    /** Gamified progress pill ("2/6") + attention pulse — used by Get Started */
    progress?: { done: number; total: number };
}

/** A collapsible group of navigation items */
export interface NavGroup {
    id: string;
    label: string;
    icon: ReactNode;
    items: NavItem[];
}

/** A visual zone containing groups — renders as a colored section divider */
export interface NavZone {
    label: string;
    color: string;
    groups: NavGroup[];
    /** Tint the zone header in the brand accent (e.g. the Practice command centre). */
    accent?: boolean;
}

export interface SidebarProps {
    /** Navigation zones */
    zones: NavZone[];
    /** Currently active route */
    activeRoute: string;
    /** Callback when a nav item is clicked */
    onNavigate: (href: string) => void;
    /** Callback to close mobile drawer after navigation */
    onMobileClose?: () => void;
    /** Force expanded mode (for mobile drawer) */
    forceExpanded?: boolean;
    /** cc-10: firm-altitude routes collapse the entity sidebar to the icon rail
     *  (the chevron still peeks it open for the current visit). */
    autoCollapse?: boolean;
    /**
     * ob-2: destinations that are reachable but have no row of their own — the
     * Simple hub-strip screens. Favorites resolve against these too, so a pin
     * outlives the item leaving the sidebar. Never rendered as nav.
     */
    pinnableItems?: NavItem[];
}

/* ─── Navigation Data ─────────────────────────────────────────── */

/**
 * Default navigation zones for a standard Karr org.
 * Every href maps to a real route in router.tsx.
 *
 * ## v4 Reorganisation Rationale
 *
 * | Change | Why |
 * |--------|-----|
 * | Receipt Scanner → Tools | It's a data-capture tool, not an overview |
 * | Fixed Assets & Loans → Assets & Liabilities | Separate financial management concern |
 * | Budgeting → Reports & Analysis | Budgeting is planning/analysis, not bookkeeping |
 * | Tasks → Tools | Tasks are universal productivity, not CRM-specific |
 * | Customer/Vendor Portal → Client Access | External-facing access, not a CRM function |
 * | Consolidation → Accounting | Multi-entity consolidation is core accounting |
 * | Settings split into 3 groups | No more dumping ground — config, integrations, AI |
 */
export function getDefaultNavZones(): NavZone[] {
    return [

        /* ═══════════════════════════════════════════
         *  ⓪ START — where you land
         * ═══════════════════════════════════════════ */
        {
            label: '',
            color: '#2563eb',
            groups: [
                {
                    id: 'grp-dashboard',
                    label: 'Dashboard',
                    icon: <LayoutDashboard size={18} />,
                    items: [
                        { id: 'dashboard', label: 'Overview', icon: <LayoutDashboard size={16} />, href: '/dashboard' },
                        // ob-4 — the ga-8 owner group dashboard had a route, and a
                        // leaf in Simple's tree, and NONE here: in Standard mode
                        // the only way to reach it was to type the URL. It is also
                        // where "Add another business" lives, so a missing leaf hid
                        // the second-business flow from every Standard owner.
                        { id: 'my-businesses', label: 'My Businesses', icon: <Building2 size={16} />, href: '/my-businesses' },
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  ① SET UP — the one-time foundation
         *  "Tell Karr who you deal with and what you sell"
         * ═══════════════════════════════════════════ */
        {
            label: '① Set up',
            color: '#64748b',
            groups: [
                {
                    id: 'grp-contacts',
                    label: 'Contacts',
                    icon: <Users size={18} />,
                    items: [
                        { id: 'contacts', label: 'Customers & Vendors', icon: <Users size={16} />, href: '/contacts' },
                    ],
                },
                {
                    id: 'grp-items',
                    label: 'Items & Services',
                    icon: <Package size={18} />,
                    items: [
                        { id: 'items', label: 'Items & Services', icon: <Package size={16} />, href: '/items' },
                    ],
                },
                {
                    id: 'grp-books',
                    label: 'Books',
                    icon: <Calculator size={18} />,
                    items: [
                        { id: 'accounts', label: 'Chart of Accounts', icon: <Landmark size={16} />, href: '/accounts' },
                        { id: 'opening-balances', label: 'Opening Balances', icon: <Wallet size={16} />, href: '/opening-balances' },
                        { id: 'journals', label: 'Journal Entries', icon: <Calculator size={16} />, href: '/journals' },
                        { id: 'dimensions', label: 'Cost Centers', icon: <Layers3 size={16} />, href: '/dimensions' },
                        // fixpage: rent, power, the audit fee, as one sheet (left Settings 3 Oct 2026).
                        { id: 'fixed-expenses', label: 'Fixed Expenses', icon: <Repeat size={16} />, href: '/fixed-expenses' },
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  ② FUND & ③ BUILD — capital, assets, people
         *  "Put money in, buy equipment, hire the team"
         * ═══════════════════════════════════════════ */
        {
            label: '② Fund & build',
            color: '#0d9488',
            groups: [
                {
                    id: 'grp-assets-liabilities',
                    label: 'Capital & Assets',
                    icon: <Building2 size={18} />,
                    items: [
                        { id: 'fixed-assets', label: 'Fixed Assets', icon: <Building2 size={16} />, href: '/fixed-assets' },
                        { id: 'maintenance', label: 'Maintenance', icon: <Wrench size={16} />, href: '/maintenance' },
                        { id: 'loans', label: 'Loans', icon: <ArrowDownUp size={16} />, href: '/loans' },
                    ],
                },
                {
                    id: 'grp-payroll',
                    label: 'People',
                    icon: <Briefcase size={18} />,
                    items: [
                        { id: 'payroll', label: 'Payroll', icon: <Briefcase size={16} />, href: '/payroll' },
                        { id: 'hr', label: 'Leave & Claims', icon: <CalendarDays size={16} />, href: '/hr' },
                        { id: 'time-tracking', label: 'Time Tracking', icon: <Clock size={16} />, href: '/time-tracking' },
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  ③ BUY — procure stock & services (incl. Make / manufacturing)
         *  "Acquire what you'll sell"
         * ═══════════════════════════════════════════ */
        {
            label: '③ Buy',
            color: '#7c3aed',
            groups: [
                {
                    id: 'grp-purchases',
                    label: 'Purchases',
                    icon: <ShoppingCart size={18} />,
                    items: [
                        { id: 'purchases', label: 'Bills', icon: <Receipt size={16} />, href: '/purchases' },
                        { id: 'rfq', label: 'RFQ / Quotes', icon: <FileQuestion size={16} />, href: '/rfq' },
                        { id: 'purchase-orders', label: 'Purchase Orders', icon: <ShoppingCart size={16} />, href: '/purchase-orders' },
                        { id: 'debit-notes', label: 'Debit Notes', icon: <ArrowDownUp size={16} />, href: '/debit-notes' },
                        { id: 'goods-received', label: 'Goods Received', icon: <PackageCheck size={16} />, href: '/goods-received' },
                        { id: 'recurring-bills', label: 'Recurring Bills', icon: <RefreshCw size={16} />, href: '/recurring-bills' },
                        { id: 'expenses', label: 'Expenses', icon: <CreditCard size={16} />, href: '/expenses' },
                    ],
                },
                {
                    id: 'grp-inventory',
                    label: 'Inventory & Make',
                    icon: <Warehouse size={18} />,
                    items: [
                        { id: 'inventory', label: 'Inventory', icon: <Warehouse size={16} />, href: '/inventory' },
                        { id: 'manufacturing', label: 'Manufacturing', icon: <Factory size={16} />, href: '/manufacturing' },
                        { id: 'quality', label: 'Quality', icon: <BadgeCheck size={16} />, href: '/quality' },
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  ④ SELL — revenue
         *  "Quote, invoice, get the order out"
         * ═══════════════════════════════════════════ */
        {
            label: '④ Sell',
            color: '#059669',
            groups: [
                /**
                 * Donations come FIRST for a non-profit, because that is the
                 * money-in a trust actually handles every day. The whole group
                 * is gated on the `ngo` module, so it prunes away for every
                 * other org type and a company's sidebar is unchanged.
                 */
                {
                    id: 'grp-donations',
                    label: 'Donations',
                    icon: <Heart size={18} />,
                    items: [
                        { id: 'donations', label: 'Donations & Receipts', icon: <Heart size={16} />, href: '/donations' },
                    ],
                },
                {
                    id: 'grp-sales',
                    label: 'Sales',
                    icon: <FileText size={18} />,
                    items: [
                        { id: 'sales', label: 'Invoices', icon: <FileText size={16} />, href: '/sales' },
                        { id: 'estimates', label: 'Estimates & Quotes', icon: <ClipboardList size={16} />, href: '/estimates' },
                        { id: 'sales-orders', label: 'Sales Orders', icon: <PackageCheck size={16} />, href: '/sales-orders' },
                        { id: 'pos', label: 'Point of Sale', icon: <ShoppingCart size={16} />, href: '/pos' },
                        { id: 'restaurant', label: 'Restaurant', icon: <Utensils size={16} />, href: '/restaurant' },
                        { id: 'kds', label: 'Kitchen', icon: <ChefHat size={16} />, href: '/kds' },
                        { id: 'food-cost', label: 'Food Cost', icon: <Calculator size={16} />, href: '/food-cost' },
                        { id: 'ecommerce', label: 'Sales channels', icon: <Store size={16} />, href: '/ecommerce' },
                        { id: 'credit-notes', label: 'Credit Notes', icon: <RotateCcw size={16} />, href: '/credit-notes' },
                        { id: 'delivery-challans', label: 'Delivery Challans', icon: <Truck size={16} />, href: '/delivery-challans' },
                        { id: 'recurring-invoices', label: 'Recurring Invoices', icon: <RefreshCw size={16} />, href: '/recurring-invoices' },
                        { id: 'subscriptions', label: 'Subscriptions', icon: <Repeat size={16} />, href: '/subscriptions' },
                        { id: 'payment-links', label: 'Payment Links', icon: <Send size={16} />, href: '/payment-links' },
                    ],
                },
                {
                    id: 'grp-crm',
                    label: 'CRM',
                    icon: <Target size={18} />,
                    items: [
                        { id: 'crm', label: 'CRM', icon: <Target size={16} />, href: '/crm' },
                    ],
                },
                {
                    // practice-door: Karr Practice's own row, in any org that switched it
                    // on (a firm, a client's books, a plain business). Not `grp-practice`:
                    // that is Office's group, which the shell lifts into the header.
                    id: 'grp-practice-desk',
                    label: 'Practice',
                    icon: <Briefcase size={18} />,
                    items: [
                        { id: 'practice-matters', label: 'Karr Practice', icon: <Briefcase size={16} />, href: '/practice/matters' },
                    ],
                },
                {
                    id: 'grp-lab',
                    label: 'Health',
                    icon: <Heart size={18} />,
                    items: [
                        // hms-3: Karr Health, every person on their own desk.
                        { id: 'hms', label: 'Karr Health', icon: <Heart size={16} />, href: '/health' },
                        { id: 'lab', label: 'Lab', icon: <FlaskConical size={16} />, href: '/health/lab' },
                    ],
                },
                {
                    id: 'grp-build',
                    label: 'Build',
                    icon: <Building2 size={18} />,
                    items: [
                        // build-1: Karr Build, every site on Karr's own project.
                        { id: 'build', label: 'Karr Build', icon: <Building2 size={16} />, href: '/build' },
                    ],
                },
                {
                    id: 'grp-travel',
                    label: 'Travel',
                    icon: <Plane size={18} />,
                    items: [
                        // travel-3: Karr Travel, every trip in eight stages.
                        { id: 'travel', label: 'Karr Travel', icon: <Plane size={16} />, href: '/travel' },
                        // travel-12: the hotel desk (Travel operations) and the partner agents (Travel partners).
                        { id: 'travel-hotels', label: 'Hotels', icon: <BedDouble size={16} />, href: '/travel/hotels' },
                        { id: 'travel-agents', label: 'Partner agents', icon: <Handshake size={16} />, href: '/travel/partners' },
                        // travel-14: the companies (Corporate travel).
                        { id: 'travel-companies', label: 'Companies', icon: <Building2 size={16} />, href: '/travel/corporate' },
                        // travel-18: fixed departures, booked from the agency's own page.
                        { id: 'travel-departures', label: 'Departures', icon: <CalendarRange size={16} />, href: '/travel/departures' },
                    ],
                },
                {
                    id: 'grp-me',
                    label: 'Me',
                    icon: <UserRound size={18} />,
                    items: [
                        // me-1: Karr Me, the person's own year (docs/KARR_ME_SPEC.md).
                        { id: 'me', label: 'Karr Me', icon: <UserRound size={16} />, href: '/me' },
                    ],
                },
                {
                    id: 'grp-fleet',
                    label: 'Fleet',
                    icon: <Truck size={18} />,
                    items: [
                        // fleet-1: Karr Fleet, vehicles on the register and drivers on payroll.
                        { id: 'fleet', label: 'Karr Fleet', icon: <Truck size={16} />, href: '/fleet' },
                    ],
                },
                {
                    id: 'grp-mandi',
                    label: 'Mandi',
                    icon: <Wheat size={18} />,
                    items: [
                        // mandi-1: Karr Mandi, the lots at the gate today (docs/KARR_MANDI_SPEC.md).
                        { id: 'mandi', label: 'Karr Mandi', icon: <Wheat size={16} />, href: '/mandi' },
                    ],
                },
                {
                    id: 'grp-stay',
                    label: 'Stay',
                    icon: <BedDouble size={18} />,
                    items: [
                        // stay-1: Karr Stay, rooms priced by the night (docs/KARR_STAY_SPEC.md).
                        { id: 'stay', label: 'Karr Stay', icon: <BedDouble size={16} />, href: '/stay' },
                    ],
                },
                {
                    id: 'grp-campus',
                    label: 'Campus',
                    icon: <GraduationCap size={18} />,
                    items: [
                        // campus-1: Karr Campus, classes, the roll and the terms (docs/KARR_SCHOOL_SPEC.md).
                        { id: 'campus', label: 'Karr Campus', icon: <GraduationCap size={16} />, href: '/campus' },
                    ],
                },
                {
                    id: 'grp-jewel',
                    label: 'Jewel',
                    icon: <Gem size={18} />,
                    items: [
                        // jewel-1: Karr Jewel, today's rate and the metal in stock (docs/KARR_JEWEL_SPEC.md).
                        { id: 'jewel', label: 'Karr Jewel', icon: <Gem size={16} />, href: '/jewel' },
                    ],
                },
                {
                    id: 'grp-studio',
                    label: 'Studio',
                    icon: <Scissors size={18} />,
                    items: [
                        // studio-1: Karr Studio's seven desks (docs/KARR_STUDIO_SPEC.md §3).
                        { id: 'studio', label: 'Book', icon: <CalendarClock size={16} />, href: '/studio' },
                        { id: 'studio-classes', label: 'Classes', icon: <CalendarDays size={16} />, href: '/studio/classes' },
                        { id: 'studio-clients', label: 'Clients', icon: <Users size={16} />, href: '/studio/clients' },
                        { id: 'studio-till', label: 'Till', icon: <ShoppingCart size={16} />, href: '/studio/till' },
                        { id: 'studio-staff', label: 'Staff', icon: <UserSquare2 size={16} />, href: '/studio/staff' },
                        { id: 'studio-morning', label: 'Morning', icon: <Sunrise size={16} />, href: '/studio/morning' },
                        { id: 'studio-page', label: 'Page', icon: <Globe size={16} />, href: '/studio/page' },
                    ],
                },
                {
                    id: 'grp-service',
                    label: 'Service',
                    icon: <Toolbox size={18} />,
                    items: [
                        // service-1: Karr Service, customer machines and their contracts (docs/KARR_SERVICE_SPEC.md).
                        { id: 'service', label: 'Karr Service', icon: <Toolbox size={16} />, href: '/service' },
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  ⑤ GET PAID & PAY — money movement
         *  "Collect from customers, settle vendors"
         * ═══════════════════════════════════════════ */
        {
            label: '⑤ Get paid & pay',
            color: '#2563eb',
            groups: [
                {
                    id: 'grp-banking',
                    label: 'Banking',
                    icon: <Landmark size={18} />,
                    items: [
                        { id: 'money-in', label: 'Money In', icon: <CreditCard size={16} />, href: '/money-in' },
                        { id: 'money-out', label: 'Money Out', icon: <Receipt size={16} />, href: '/money-out' },
                        { id: 'pay-week', label: 'Payments Due', icon: <Wallet size={16} />, href: '/pay-week' },
                        { id: 'transfers', label: 'Transfers', icon: <ArrowLeftRight size={16} />, href: '/transfers' },
                        { id: 'bank-statements', label: 'Bank Statements', icon: <FileSpreadsheet size={16} />, href: '/bank-statements' },
                        { id: 'bank-reconciliation', label: 'Bank Reconciliation', icon: <Link2 size={16} />, href: '/bank-reconciliation' },
                        { id: 'bank-feeds', label: 'Bank Feeds', icon: <Landmark size={16} />, href: '/bank-feeds' },
                    ],
                },
                {
                    id: 'grp-receivables',
                    label: 'Receivables & Payables',
                    icon: <ArrowDownUp size={18} />,
                    items: [
                        { id: 'receivables', label: 'Receivables & Payables', icon: <ArrowDownUp size={16} />, href: '/receivables' },
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  ⑥ COMPLY — taxes & statutory
         *  "Stay on the right side of the law"
         * ═══════════════════════════════════════════ */
        {
            label: '⑥ Comply',
            color: '#dc2626',
            groups: [
                {
                    id: 'grp-tax',
                    label: 'Tax & Compliance',
                    icon: <Shield size={18} />,
                    items: [
                        { id: 'gst-filing', label: 'GST Filing Cockpit', icon: <Wand2 size={16} />, href: '/gst-filing' },
                        { id: 'itr-filing', label: 'ITR Filing Cockpit', icon: <FileText size={16} />, href: '/itr-filing' },
                        // auditflow: advance tax is the client's own income-tax
                        // planning for the running year (section 211
                        // instalments, 234B/234C interest), so it sits with the
                        // income-tax cockpit. It used to be a tab of the tax
                        // audit page, which is the auditor's report on a year
                        // that has ended: a different job, a different reader.
                        { id: 'advance-tax', label: 'Advance tax', icon: <TrendingUp size={16} />, href: '/advance-tax' },
                        { id: 'tds-filing', label: 'TDS Filing Cockpit', icon: <Landmark size={16} />, href: '/tds-filing' },
                        // psf-1: the fourth cockpit, and it belongs beside its
                        // siblings — "file this month's returns" is one job
                        // whichever department is on the other end. Owned by the
                        // PAYROLL module (no payroll, no provident fund to file),
                        // which is why it sits in Comply while `modules.ts` gates
                        // it from somewhere else entirely.
                        { id: 'payroll-filing', label: 'Payroll Filing Cockpit', icon: <Briefcase size={16} />, href: '/payroll-filing' },
                        // sx-3: the export declaration. Sits with the cockpits
                        // because "declare this month" is the same job as
                        // "file this month", whoever is on the other end.
                        { id: 'softex', label: 'Software Exports', icon: <Globe size={16} />, href: '/softex' },
                        { id: 'gst-tds', label: 'GST & TDS (Live)', icon: <Landmark size={16} />, href: '/gst-tds' },
                        { id: 'compliance-reports', label: 'Compliance Reports', icon: <Shield size={16} />, href: '/compliance-reports' },
                        { id: 'compliance', label: 'Compliance Intelligence', icon: <ClipboardCheck size={16} />, href: '/compliance' },
                        { id: 'books-health', label: 'Books Health', icon: <BadgeCheck size={16} />, href: '/books-health' },
                        { id: 'notices', label: 'Notices & Demands', icon: <Gavel size={16} />, href: '/notices' },
                        { id: 'statutory-registers', label: 'Statutory Registers', icon: <BookMarked size={16} />, href: '/statutory-registers' },
                        { id: 'roc', label: 'ROC Compliance', icon: <MapPinned size={16} />, href: '/roc' },
                        // The tax audit (Form 3CD) is not here: it is a chapter
                        // of Audit, under Review, because it is the auditor's
                        // report on a year that has ended, not a filing the
                        // client runs.
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  ⑦ REVIEW — see how you're doing
         *  "Read the numbers, plan ahead"
         * ═══════════════════════════════════════════ */
        {
            label: '⑦ Review',
            color: '#ea580c',
            groups: [
                /*
                 * auditflow (16 Sep 2026): ONE door for the audit of the
                 * business on screen, and the firm's list beside it.
                 *
                 * This group used to carry four leaves: "Close the year", "This
                 * business's audit", "Tax audit (Form 3CD)" and the firm's list.
                 * The first three were one job cut into three pages, each with
                 * its own year picker, and nothing on any of them decided which
                 * audit this business owed. Founder, 16 Sep 2026: "it is so
                 * cluttered, it is not following a flow ... once we go to Audit
                 * it gives us an option whether it is a tax audit or a stat
                 * audit ... and if it is a tax audit then the tax audit workflow
                 * flows."
                 *
                 * So `/audit` decides the audit and runs it, in the order it
                 * happens: who audits, the books, the 3CD, sign, the file, the
                 * portal. The desk and the 3CD are chapters of it. The old
                 * addresses (`/audit-desk`, `/tax-audit`) redirect there, so a
                 * bookmark or a stored fix button still lands; they have no leaf
                 * of their own, because two leaves for one screen is two homes.
                 */
                {
                    id: 'grp-audit',
                    label: 'Audit',
                    icon: <ShieldCheck size={18} />,
                    items: [
                        { id: 'audit', label: 'Audit', icon: <ShieldCheck size={16} />, href: '/audit' },
                        // aud-10: the firm's own list, across every client it audits.
                        { id: 'my-audits', label: 'Every business I audit', icon: <ClipboardCheck size={16} />, href: '/my-audits' },
                    ],
                },
                {
                    id: 'grp-reports',
                    label: 'Reports & Analysis',
                    icon: <BarChart3 size={18} />,
                    items: [
                        { id: 'reports', label: 'Reports', icon: <BarChart3 size={16} />, href: '/reports' },
                        { id: 'accruals', label: 'Month-end (Accruals)', icon: <CalendarDays size={16} />, href: '/accruals' },
                        { id: 'period-locks', label: 'Close & Lock Periods', icon: <Lock size={16} />, href: '/period-locks' },
                        { id: 'report-builder', label: 'Report Builder', icon: <Table2 size={16} />, href: '/report-builder' },
                        { id: 'budgeting', label: 'Budgeting & Forecasts', icon: <Target size={16} />, href: '/budgeting' },
                        { id: 'projections', label: 'Projections & Bank Finance', icon: <Landmark size={16} />, href: '/projections' },
                        // Donations is NOT here: it is where a trust enters money,
                        // not something it reads back, so it lives above Sales in
                        // the money-in zone. NGO Reports genuinely is a report
                        // (Receipts & Payments, Fund utilisation, FCRA, Income & 85%)
                        // and stays.
                        { id: 'ngo-reports', label: 'NGO Reports', icon: <FileText size={16} />, href: '/ngo-reports' },
                    ],
                },
                {
                    id: 'grp-advanced',
                    label: 'Multi-entity & FX',
                    icon: <Globe size={18} />,
                    items: [
                        { id: 'multi-currency', label: 'Multi-Currency', icon: <Globe size={16} />, href: '/multi-currency' },
                        { id: 'consolidation', label: 'Consolidation', icon: <Building2 size={16} />, href: '/consolidation' },
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  TOOLS — cross-cutting, used at any stage
         * ═══════════════════════════════════════════ */
        {
            label: 'Tools',
            color: '#ea580c',
            groups: [
                {
                    id: 'grp-tools',
                    label: 'Tools',
                    icon: <Wrench size={18} />,
                    items: [
                        { id: 'projects', label: 'Projects', icon: <FolderKanban size={16} />, href: '/projects' },
                        { id: 'documents', label: 'Documents', icon: <FileStack size={16} />, href: '/documents' },
                        { id: 'helpdesk', label: 'Helpdesk', icon: <LifeBuoy size={16} />, href: '/helpdesk' },
                        { id: 'approvals', label: 'Approvals', icon: <ShieldCheck size={16} />, href: '/approvals' },
                        { id: 'receipt-scanner', label: 'Receipt Scanner', icon: <ScanLine size={16} />, href: '/receipt-scanner' },
                        { id: 'tasks', label: 'Tasks', icon: <ListChecks size={16} />, href: '/tasks' },
                        { id: 'ai-tools', label: 'AI Assistant', icon: <Bot size={16} />, href: '/ai-tools' },
                        // market — the shelf of add-ons, their prices, one button each.
                        { id: 'marketplace', label: 'Marketplace', icon: <Store size={16} />, href: '/marketplace' },
                    ],
                },
                {
                    id: 'grp-client-access',
                    label: 'Client Access',
                    icon: <UserCircle size={18} />,
                    items: [
                        { id: 'portal', label: 'Customer / Vendor Portal', icon: <UserCircle size={16} />, href: '/portal' },
                    ],
                },
            ],
        },

        /* ═══════════════════════════════════════════
         *  SYSTEM — Team & Practice (Settings moved to header gear ⚙️)
         * ═══════════════════════════════════════════ */
        {
            label: '',
            color: '#64748b',
            groups: [
                {
                    id: 'grp-team',
                    label: 'Team',
                    icon: <Users size={18} />,
                    items: [
                        { id: 'collaboration', label: 'Access', icon: <UserCircle size={16} />, href: '/collaboration' },
                        { id: 'audit-trail', label: 'Audit Trail', icon: <ShieldCheck size={16} />, href: '/audit-trail' },
                    ],
                },
                {
                    id: 'grp-practice',
                    label: 'Practice',
                    icon: <Briefcase size={18} />,
                    items: [
                        { id: 'my-work', label: 'My work', icon: <ListChecks size={16} />, href: '/my-work' },
                        { id: 'portfolio', label: 'Board', icon: <Building2 size={16} />, href: '/portfolio' },
                        { id: 'practice-crm', label: 'CRM', icon: <FolderKanban size={16} />, href: '/practice-crm' },
                        { id: 'requests', label: 'Client Requests', icon: <FileQuestion size={16} />, href: '/requests' },
                        { id: 'partner', label: 'Partner Program', icon: <Gift size={16} />, href: '/partner' },
                    ],
                },
                {
                    id: 'grp-integrations',
                    label: 'Integrations',
                    icon: <Link2 size={18} />,
                    // connect-1: three lines. The mailbox, WhatsApp and the AI key are
                    // tiles on Integrations now (each lands on its Settings tab), and
                    // keys, webhooks and the API reference live under Developers.
                    items: [
                        { id: 'integrations', label: 'Integrations', icon: <Link2 size={16} />, href: '/integrations' },
                        { id: 'developers', label: 'Developers', icon: <Code2 size={16} />, href: '/developers' },
                        { id: 'import', label: 'Import Data', icon: <ArrowDownUp size={16} />, href: '/import' },
                    ],
                },
            ],
        },
    ];
}

/* ─── Backward Compat ─────────────────────────────────────────── */

/**
 * @deprecated Use getDefaultNavZones() instead.
 * Flattens zones into a flat group list for legacy consumers.
 */
export function getDefaultNavGroups(): { label: string; items: NavItem[] }[] {
    return getDefaultNavZones().flatMap(zone =>
        zone.groups.map(g => ({ label: g.label, items: g.items }))
    );
}

/* ═══════════════════════════════════════════════════════════════
 *  EXPERIENCE-LEVEL AWARE NAVIGATION
 *  This is the core of Karr's adaptive UI.
 * ═══════════════════════════════════════════════════════════════ */

import type { ExperienceLevel } from '../config/experienceLevel';
import { isNavItemVisible, getNavItemLabel } from '../config/experienceLevel';
import type { BusinessNature } from '../config/businessNature';
import { isNavItemVisibleByModule } from '../config/modules';
import { simpleNavForNature } from '../config/simpleNav';

/**
 * Resolve a lucide icon name from the React-free Simple-nav config. Unknown
 * names fall back to a neutral glyph rather than crashing the sidebar — a typo
 * in a label should never blank someone's navigation.
 */
const SIMPLE_NAV_ICONS: Record<string, typeof LayoutDashboard> = {
    LayoutDashboard, FileText, ShoppingCart, Receipt, Warehouse, Users, FileCheck, BarChart3, Briefcase,
};
function simpleNavIcon(name: string, size: number) {
    const Icon = SIMPLE_NAV_ICONS[name] ?? ListChecks;
    return <Icon size={size} />;
}

// applyNatureGating is RETIRED (mod-6): business nature no longer hides nav
// items — the activation layer (applyModuleGating, below) is the single nav
// gate, and nature now SEEDS the module set (docs/MODULES.md Sec. 4/Sec. 5). The Simple
// tree still curates its own leaf list by nature (getBusinessOwnerZones).

/**
 * Nav items that only make sense for Indian companies with share capital.
 * Kept here (not in experienceLevel/businessNature) because this gate is about
 * the ORG TYPE.
 *
 * `statutory-registers` came OUT of this set in sigreg. The screen is now
 * entity-aware: a company sees the Companies Act registers, and everything else
 * sees a Register of Owners built on `equity_holdings`, where its proprietor,
 * partners or trustees live. Both see the Signatures tab, and a signature
 * belongs to a person on a register, so hiding the screen from a firm hid the
 * only place its partners could be asked for one.
 */
const COMPANY_ONLY_NAV_ITEM_IDS = new Set(['roc']);
const COMPANY_NAV_ORG_TYPES = IN_COMPANY_ORG_TYPES;

/**
 * Drop nav items that require a company org type (e.g. Statutory Registers
 * for a sole proprietorship), then prune groups/zones left empty. Items are
 * HIDDEN while the org type is unknown so the nav never flashes-then-removes.
 */
export function applyOrgTypeGating(zones: NavZone[], orgType?: string | null): NavZone[] {
    const isCompany = COMPANY_NAV_ORG_TYPES.has(orgType ?? '');
    if (isCompany) return zones;
    return zones
        .map(zone => ({
            ...zone,
            groups: zone.groups
                .map(group => ({
                    ...group,
                    items: group.items.filter(item => !COMPANY_ONLY_NAV_ITEM_IDS.has(item.id)),
                }))
                .filter(group => group.items.length > 0),
        }))
        .filter(zone => zone.groups.length > 0);
}

/**
 * Drop nav items whose module is not active for the org, then prune empty
 * groups/zones (the activation layer, mod-2 · docs/MODULES.md Sec. 3). Core leaves
 * are always kept; an unclassified id fails OPEN. Composes with (does not
 * replace) nature/level/RBAC gating — a leaf must clear every layer to show.
 *
 * For a LEGACY org (`enabled_modules` undefined) the resolver returns every
 * operational module (minus the goods-only ones for a service org), so this is
 * a no-op over the already nature-gated tree — legacy nav stays pixel-identical.
 */
export function applyModuleGating(zones: NavZone[], activeModuleIds: ReadonlySet<string>): NavZone[] {
    return zones
        .map(zone => ({
            ...zone,
            groups: zone.groups
                .map(group => ({
                    ...group,
                    items: group.items.filter(item => isNavItemVisibleByModule(item.id, activeModuleIds)),
                }))
                .filter(group => group.items.length > 0),
        }))
        .filter(zone => zone.groups.length > 0);
}

/**
 * Simple mode's navigation — ob-2 (docs/ONBOARDING.md O4/O5).
 *
 * Six rows, in the order money moves — Home · Sales · Purchases ·
 * People & Products · Taxes · Reports — plus at most two the business nature
 * asks for (Point of Sale, Stock). Hard cap: eight.
 *
 * The shape lives in `src/shared/config/simpleNav.ts`, which also records the
 * screen every leaf that LEFT this nav now lives inside; this function only
 * renders it. One zone with no label: a Simple user gets a flat list of places,
 * not a taxonomy of stages.
 */
function getBusinessOwnerZones(
    nature?: BusinessNature | null,
    orgType?: string | null,
): NavZone[] {
    return [{
        label: '',
        color: '#0d9488',
        groups: simpleNavForNature(nature, orgType).map(entry => ({
            id: entry.groupId,
            label: entry.label,
            icon: simpleNavIcon(entry.iconName, 18),
            items: entry.items.map(item => ({
                id: item.id,
                label: item.label,
                icon: simpleNavIcon(item.iconName, 16),
                href: item.href,
            })),
        })),
    }];
}

/**
 * The pre-ob-2 Simple tree — 34 leaves, "the full product with different
 * labels". Kept as the historical record of what the surface used to carry:
 * `simpleNav.test.ts` walks it and asserts every one of those leaves is still
 * reachable from the six rows. Never rendered.
 */
export function getLegacyBusinessOwnerZones(nature?: BusinessNature | null): NavZone[] {
    return [
        /* Home */
        {
            label: '',
            color: '#2563eb',
            groups: [
                {
                    id: 'grp-dashboard',
                    label: 'Home',
                    icon: <LayoutDashboard size={18} />,
                    items: [
                        { id: 'dashboard', label: 'Overview', icon: <LayoutDashboard size={16} />, href: '/dashboard' },
                        // ga-8 — owners with more than one business get a group home.
                        { id: 'my-businesses', label: 'My Businesses', icon: <Building2 size={16} />, href: '/my-businesses' },
                    ],
                },
            ],
        },

        /* ① Set up — who you deal with, what you sell */
        {
            label: '① Set up',
            color: '#64748b',
            groups: [
                // Starting Balances is a ONE-TIME task, not a destination — it
                // lives in the Get Started guide (and Settings afterwards), never
                // permanently in navigation.
                {
                    id: 'grp-contacts',
                    label: 'Customers & Vendors',
                    icon: <Users size={18} />,
                    items: [
                        { id: 'contacts', label: 'Customers & Vendors', icon: <Users size={16} />, href: '/contacts' },
                    ],
                },
                {
                    id: 'grp-items',
                    label: 'Products & Services',
                    icon: <Package size={18} />,
                    items: [
                        { id: 'items', label: 'Products & Services', icon: <Package size={16} />, href: '/items' },
                    ],
                },
                {
                    id: 'grp-team',
                    label: 'Your team',
                    icon: <Users size={18} />,
                    items: [
                        { id: 'collaboration', label: 'Access', icon: <UserCircle size={16} />, href: '/collaboration' },
                        // Audit Trail is accountant furniture — Standard/Expert only.
                    ],
                },
            ],
        },

        /* ② Buy — what you spend on (goods-only Inventory hides for services) */
        {
            label: '② Buy',
            color: '#7c3aed',
            groups: [
                {
                    id: 'grp-buy',
                    label: 'Spending',
                    icon: <Receipt size={18} />,
                    items: [
                        { id: 'expenses', label: 'Bills & Expenses', icon: <CreditCard size={16} />, href: '/expenses' },
                        { id: 'pay-week', label: 'What to Pay', icon: <Wallet size={16} />, href: '/pay-week' },
                        { id: 'money-out', label: 'Payments Made', icon: <Receipt size={16} />, href: '/money-out' },
                        { id: 'receipt-scanner', label: 'Scan Receipts', icon: <ScanLine size={16} />, href: '/receipt-scanner' },
                    ],
                },
                {
                    id: 'grp-inventory',
                    label: 'Inventory',
                    icon: <Warehouse size={18} />,
                    items: [
                        { id: 'inventory', label: 'Inventory', icon: <Warehouse size={16} />, href: '/inventory' },
                        // Manufacturing + Quality are Expert-grade modules — in Simple mode they'd
                        // leak to every shopkeeper. Show them only when the org actually makes
                        // goods; a Simple manufacturer keeps them, a retailer/service/unset org
                        // doesn't (nature-gating below only hides goods screens for 'service').
                        ...(nature === 'manufacturing' ? [
                            { id: 'manufacturing', label: 'Manufacturing', icon: <Factory size={16} />, href: '/manufacturing' },
                            { id: 'quality', label: 'Quality', icon: <BadgeCheck size={16} />, href: '/quality' },
                        ] : []),
                    ],
                },
            ],
        },

        /* ③ Sell — bring in revenue */
        {
            label: '③ Sell',
            color: '#059669',
            groups: [
                {
                    id: 'grp-sell',
                    label: 'Sales',
                    icon: <FileText size={18} />,
                    items: [
                        { id: 'sales', label: 'Invoices', icon: <FileText size={16} />, href: '/sales' },
                        { id: 'sales-orders', label: 'Orders', icon: <PackageCheck size={16} />, href: '/sales-orders' },
                        { id: 'pos', label: 'Point of Sale', icon: <ShoppingCart size={16} />, href: '/pos' },
                        { id: 'restaurant', label: 'Restaurant', icon: <Utensils size={16} />, href: '/restaurant' },
                        { id: 'kds', label: 'Kitchen', icon: <ChefHat size={16} />, href: '/kds' },
                        { id: 'food-cost', label: 'Food Cost', icon: <Calculator size={16} />, href: '/food-cost' },
                        { id: 'payment-links', label: 'Collect Online', icon: <Send size={16} />, href: '/payment-links' },
                        // ga-7 — Simple nav gains Refunds (plain name for credit notes).
                        { id: 'credit-notes', label: 'Refunds', icon: <ArrowLeftRight size={16} />, href: '/credit-notes' },
                    ],
                },
            ],
        },

        /* ④ Get paid — money landing in your account */
        {
            label: '④ Get paid',
            color: '#2563eb',
            groups: [
                {
                    id: 'grp-getpaid',
                    label: 'Money',
                    icon: <CreditCard size={18} />,
                    items: [
                        { id: 'money-in', label: 'Payments Received', icon: <CreditCard size={16} />, href: '/money-in' },
                        // ga-7 — Simple nav gains Move Money + Loans & EMIs.
                        { id: 'transfers', label: 'Move Money', icon: <ArrowLeftRight size={16} />, href: '/transfers' },
                        { id: 'loans', label: 'Loans & EMIs', icon: <Landmark size={16} />, href: '/loans' },
                    ],
                },
            ],
        },

        /* ⑤ Review — how you're doing */
        {
            label: '⑤ Review',
            color: '#ea580c',
            groups: [
                {
                    id: 'grp-review',
                    label: 'Reports & Help',
                    icon: <BarChart3 size={18} />,
                    // ga-7 — Simple nav completion: gains Month-end, Things You Own,
                    // Tax Filing and Rules That Apply; loses accountant furniture
                    // (Close & Lock Periods + Approvals — period-close folds into the
                    // Month-end ritual).
                    items: [
                        { id: 'reports', label: 'Reports', icon: <BarChart3 size={16} />, href: '/reports' },
                        { id: 'accruals', label: 'Month-end', icon: <CalendarDays size={16} />, href: '/accruals' },
                        { id: 'fixed-assets', label: 'Things You Own', icon: <Building2 size={16} />, href: '/fixed-assets' },
                        { id: 'gst-filing', label: 'Tax Filing', icon: <FileCheck size={16} />, href: '/gst-filing' },
                        { id: 'compliance', label: 'Rules That Apply', icon: <ShieldCheck size={16} />, href: '/compliance' },
                        { id: 'books-health', label: 'Books Checkup', icon: <BadgeCheck size={16} />, href: '/books-health' },
                        { id: 'audit', label: 'Audit', icon: <ShieldCheck size={16} />, href: '/audit' },
                        { id: 'ai-tools', label: 'AI Help', icon: <Bot size={16} />, href: '/ai-tools' },
                        { id: 'marketplace', label: 'Marketplace', icon: <Store size={16} />, href: '/marketplace' },
                        { id: 'partner', label: 'Refer & Earn', icon: <Gift size={16} />, href: '/partner' },
                    ],
                },
            ],
        },
    ];
}

/**
 * Get navigation zones adapted for the user's experience level. The activation
 * layer (applyModuleGating in AppLayout) is what hides modules the org hasn't
 * turned on — this function no longer gates by business nature (mod-6). `nature`
 * is still used to curate the Simple (business_owner) tree's own leaf list.
 *
 * @param level  - The user's experience level
 * @param nature - The org's business nature (curates the Simple tree only)
 * @returns NavZone[] for that level
 *
 * @example
 *   // In AppLayout:
 *   const zones = getNavZonesForLevel('business_owner', currentOrg?.business_nature);
 *
 *   const zones = getNavZonesForLevel('expert');
 *   // → Full lifecycle navigation with all accounting, modules, practice
 */
export function getNavZonesForLevel(
    level: ExperienceLevel,
    nature?: BusinessNature | null,
    /**
     * Only Simple reads this, and only to put Donations first on the money-in
     * row for a non-profit. Omit it and the tree is exactly what it always was.
     */
    orgType?: string | null,
): NavZone[] {
    // Business owner gets a completely different, simplified navigation
    if (level === 'business_owner') {
        return getBusinessOwnerZones(nature, orgType);
    }

    // Standard/Expert: start from full nav, then filter
    const fullZones = getDefaultNavZones();

    // ga-7: Standard (= expert) sees everything; the retired finance_manager
    // collapses into it.
    if (level === 'expert' || level === 'finance_manager') {
        return fullZones;
    }

    // Anything else: filter out expert-only items, apply label overrides.
    // Unreachable today — the two lines above return for every live level — and
    // kept only so an added level fails soft instead of showing the full nav.
    const filtered = fullZones
        .map(zone => ({
            ...zone,
            groups: zone.groups
                .map(group => ({
                    ...group,
                    items: group.items
                        .filter(item => isNavItemVisible(item.id, level))
                        .map(item => ({
                            ...item,
                            label: getNavItemLabel(item.id, item.label, level),
                        })),
                }))
                .filter(group => group.items.length > 0), // Remove empty groups
        }))
        .filter(zone => zone.groups.length > 0); // Remove empty zones

    return filtered;
}

// wv-2 removed `withPracticeOnTop`: a practice org's sidebar no longer carries
// the Practice group at all — the office lives in the header pill → /portfolio
// (cc-10/cc-11). office-one then dropped wv-2's firm-books toggle too: the
// sidebar is entity navigation, full stop, and AppLayout simply does not render
// it at the office altitude.
